import { useState, useEffect, ReactNode } from "react";
import LoginPage from "@/pages/auth/LoginPage";
import { authApi } from "@/lib/api-client";
import { useAuthStore, AuthUser } from "@/stores/auth.store";
import { tokenManager } from "@/lib/token-manager";

interface AuthGuardProps {
    children: ReactNode;
}

function isTokenExpired(token: string, offsetSeconds = 30): boolean {
    try {
        const parts = token.split('.');
        if (parts.length !== 3) return false;
        const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
        if (!payload.exp) return false;
        const now = Math.floor(Date.now() / 1000);
        return payload.exp <= now + offsetSeconds;
    } catch {
        return false;
    }
}

export function AuthGuard({ children }: AuthGuardProps) {
    const { isAuthenticated, accessToken, user, setUser, logout } = useAuthStore();
    const [isChecking, setIsChecking] = useState(true);
    const [verified, setVerified] = useState(false);

    const isLandingPage = window.location.pathname === '/landingpage' || window.location.pathname.startsWith('/landingpage/');

    useEffect(() => {
        if (isLandingPage) return;

        const verifyAuth = async () => {
            if (!accessToken || !user) {
                setVerified(false);
                setIsChecking(false);
                return;
            }

            try {
                // Kiểm tra hạn JWT chủ động: nếu đã hết hạn hoặc sắp hết hạn, refresh trước khi gọi /auth/me
                if (isTokenExpired(accessToken)) {
                    const refreshed = await tokenManager.refreshAccessToken();
                    if (!refreshed) {
                        logout();
                        setVerified(false);
                        setIsChecking(false);
                        return;
                    }
                }

                // Xác thực token với backend bằng API /auth/me
                const response = await authApi.me();
                if (response.success && response.data) {
                    // Cập nhật user data mới nhất từ server vào Zustand store
                    setUser(response.data as AuthUser);
                    setVerified(true);
                } else {
                    // Token không hợp lệ - xóa dữ liệu
                    logout();
                    setVerified(false);
                }
            } catch (err: any) {
                // Phân biệt chính xác giữa lỗi mạng (offline) và lỗi xác thực bị từ chối (401/403)
                const status = err?.response?.status;
                const isAuthRejection = status === 401 || status === 403;
                const isOffline = !isAuthRejection && (
                    (typeof navigator !== 'undefined' && !navigator.onLine) ||
                    err?.code === 'ERR_NETWORK' ||
                    !err?.response
                );

                if (isOffline && user && accessToken) {
                    // Chỉ cho phép duy trì giao diện khi thực sự mất mạng (offline) và đã có cache
                    setVerified(true);
                } else {
                    // Khi server từ chối xác thực (401/403 hoặc refresh thất bại): Buộc logout và khóa cửa
                    logout();
                    setVerified(false);
                }
            }

            setIsChecking(false);
        };

        verifyAuth();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Nếu là trang public landing page, cho phép truy cập luôn mà không cần đăng nhập và không cần check loading
    if (isLandingPage) {
        return <>{children}</>;
    }

    // Hiển thị loading khi đang kiểm tra
    if (isChecking) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-100">
                <div className="text-center">
                    <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-indigo-600 border-r-transparent"></div>
                    <p className="mt-4 text-slate-600">Đang kiểm tra phiên đăng nhập...</p>
                </div>
            </div>
        );
    }

    // Bỏ check landing page ở đây vì đã check ở trên cùng
    // Nếu chưa xác thực, hiển thị trang đăng nhập
    if (!isAuthenticated || !verified) {
        return <LoginPage />;
    }

    // Đã xác thực, render ứng dụng
    return <>{children}</>;
}
